import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["associateCrmObjectComponent", "onSubmitAssociation", "isConfirmButtonEnabled", "hasLegacyDealAmountCalculation"];
import { UNIT_COST } from 'customer-data-objects/lineItem/PropertyNames';
import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { ASSOCIATE_TAB_ID, CREATE_TAB_ID, DEAL_AMOUNT_FIELD_NAME } from '../../constants/associations';
import { toObjectTypes } from '../../constants/types';
import { AppContext } from '../../contexts/app/context';
import { AssociationModalPropsContext } from '../../contexts/associationModal/PropsContext';
import { defaultAssociationModalContextProps } from '../../contexts/associationModal/constants';
import { AssociationModalContext, AssociationModalContextProvider } from '../../contexts/associationModal/context';
import { PropertiesContext } from '../../contexts/properties/context';
import PropertiesContextHOC from '../../hoc/PropertiesContextHOC';
import { calculateUnitCost } from '../../utils/lineItems';
import { createCrmObjectFormValidator } from '../../validators/createCrmObjectFormValidator';
import AssociationModalBody from './AssociationModalBody';
import { jsx as _jsx } from "react/jsx-runtime";
function AssociationModal({
  associateCrmObjectComponent,
  onSubmitAssociation,
  isConfirmButtonEnabled = true
}) {
  const {
    isContactsPage,
    crmObjectType,
    usageTracker,
    createObjectCallback,
    showModal,
    closeModal
  } = useContext(AssociationModalPropsContext);
  const {
    selectors: {
      isDraft
    }
  } = useContext(AppContext);
  const {
    formFields,
    crmObjectAssociations,
    newLineItems,
    currencyExchangeRates,
    homeCurrency,
    currency,
    crmObjectCreationFormErrorMessage
  } = useContext(AssociationModalContext);
  const {
    isValidating,
    validationErrors
  } = useContext(PropertiesContext);
  const {
    associationModalActions
  } = useContext(AssociationModalContext);
  const [selectedTab, setSelectedTab] = useState(ASSOCIATE_TAB_ID);
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingFailed = useMemo(() => !!crmObjectCreationFormErrorMessage, [crmObjectCreationFormErrorMessage]);
  const prevIsSubmittingFailedValue = useRef(isSubmittingFailed);
  const prevIsSelectedTab = useRef(selectedTab);
  const createNewCrmObject = properties => {
    associationModalActions.createCrmObject({
      data: properties,
      successCallback: createObjectCallback,
      crmObjectAssociations,
      newLineItems: newLineItems.map(lineItem => {
        const exchangedUnitCost = calculateUnitCost({
          currencyExchangeRates,
          fromCurrencyCode: homeCurrency,
          toCurrencyCode: currency,
          initialPrice: lineItem[UNIT_COST]
        });
        return Object.assign({}, lineItem, {
          [UNIT_COST]: exchangedUnitCost ? exchangedUnitCost.toString() : ''
        });
      }),
      usageTracker,
      crmObjectType,
      cb: closeModal
    });
  };
  const onClose = () => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associate${_capitalize(crmObjectType)}`],
      type: crmObjectType
    }, ACTIONS.general.closeAssociationPanel));
    closeModal();
  };
  const onSelectedTabChange = id => setSelectedTab(id);
  const isSubmitButtonEnabled = useMemo(() => {
    if (selectedTab === ASSOCIATE_TAB_ID) {
      return isConfirmButtonEnabled;
    }
    const emptyRequiredFields = formFields.filter(field => field.required && !_get(field, 'value'));
    const validationIssues = Object.values(validationErrors).filter(key => Object.keys(key).length);
    return emptyRequiredFields.length === 0 && validationIssues.length === 0 && !isValidating;
  }, [formFields, selectedTab, isConfirmButtonEnabled, validationErrors, isValidating]);
  const getOnSubmitAction = e => {
    e.preventDefault();
    if (selectedTab === ASSOCIATE_TAB_ID && typeof onSubmitAssociation === 'function') {
      onSubmitAssociation();
      setIsLoading(true);
      return;
    }
    if (selectedTab === CREATE_TAB_ID) {
      const validatedFormFields = createCrmObjectFormValidator(formFields);
      const invalidFormFields = validatedFormFields.filter(el => !el.valid);
      if (invalidFormFields.length) {
        associationModalActions.setFormFields(validatedFormFields);
      } else {
        const newCrmObjectValues = {};
        formFields.map(el => newCrmObjectValues[el.name] = el.value);
        if (crmObjectType === toObjectTypes.deal && newLineItems.length) {
          const currentAmount = Number(newCrmObjectValues[DEAL_AMOUNT_FIELD_NAME]);

          // delete amount field if user set deal amount reference to manual
          // in this case it should be empty
          if (!currentAmount) delete newCrmObjectValues[DEAL_AMOUNT_FIELD_NAME];
        }
        createNewCrmObject(newCrmObjectValues);
        setIsLoading(true);
      }
    }
  };
  useEffect(() => {
    if (prevIsSelectedTab.current !== selectedTab && showModal) {
      prevIsSelectedTab.current = selectedTab;
      usageTracker.trackInteraction(Object.assign({
        screen: SCREENS[`associatePannel${_capitalize(selectedTab.toString())}Tab`],
        type: crmObjectType
      }, ACTIONS.general.switchAssociationPanelTab));
    }
  }, [selectedTab, prevIsSelectedTab, crmObjectType, usageTracker, showModal]);
  useEffect(() => {
    if (prevIsSubmittingFailedValue.current === false && isSubmittingFailed) {
      setIsLoading(false);
    }
    if (prevIsSubmittingFailedValue.current !== isSubmittingFailed) {
      prevIsSubmittingFailedValue.current = isSubmittingFailed;
    }
  }, [isSubmittingFailed]);

  // in draft mode we switch default tab value to 'create'
  // as we need only create flow
  // user can`t change tab in draft mode for guests
  useEffect(() => {
    if (isDraft && isContactsPage && showModal) {
      setSelectedTab(CREATE_TAB_ID);
    }
  }, [isDraft, isContactsPage, showModal]);

  // turn off loader on hide modal
  useEffect(() => {
    if (!showModal) {
      setSelectedTab(ASSOCIATE_TAB_ID);
      setIsLoading(false);
      prevIsSelectedTab.current = ASSOCIATE_TAB_ID;
      prevIsSubmittingFailedValue.current = false;
    }
  }, [showModal]);
  if (!showModal) return null;
  return /*#__PURE__*/_jsx(AssociationModalBody, {
    isLoading: isLoading,
    isSubmittingFailed: isSubmittingFailed,
    isSubmitButtonEnabled: !!isSubmitButtonEnabled,
    associateCrmObjectComponent: associateCrmObjectComponent,
    onClose: onClose,
    onSelectedTabChange: onSelectedTabChange,
    getOnSubmitAction: getOnSubmitAction,
    selectedTab: selectedTab
  });
}
const WrappedAssociationsPanel = _ref => {
  let {
      associateCrmObjectComponent,
      onSubmitAssociation,
      isConfirmButtonEnabled,
      hasLegacyDealAmountCalculation
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const definedProps = _omitBy(rest, _isNil);
  const apiWrapper = _omitBy(rest.apiWrapper, _isNil);
  const properties = Object.assign({}, defaultAssociationModalContextProps, definedProps, {
    apiWrapper: Object.assign({}, defaultAssociationModalContextProps.apiWrapper, apiWrapper)
  });
  return /*#__PURE__*/_jsx(AssociationModalPropsContext.Provider, {
    value: properties,
    children: /*#__PURE__*/_jsx(AssociationModalContextProvider, {
      hasLegacyDealAmountCalculation: hasLegacyDealAmountCalculation,
      children: /*#__PURE__*/_jsx(PropertiesContextHOC, {
        apiWrapper: {
          fetchExternalOptions: properties.apiWrapper.fetchExternalOptions,
          validateProperty: properties.apiWrapper.validateProperty,
          fetchCardProperties: properties.apiWrapper.fetchCardProperties,
          fetchPropertiesDetails: properties.apiWrapper.fetchPropertiesDetails
        },
        children: /*#__PURE__*/_jsx(AssociationModal, {
          associateCrmObjectComponent: associateCrmObjectComponent,
          onSubmitAssociation: onSubmitAssociation,
          isConfirmButtonEnabled: isConfirmButtonEnabled
        })
      })
    })
  });
};
export default WrappedAssociationsPanel;